// Program editor (plan R4, docs/oct27-work/plans/R4.md §4.7): the pure part of
// the lecture order, the quiz clean-up before a save, and how a failed save
// becomes field errors and sentences. No React.
//
// THE LECTURE ORDER IS CHANGED ONLY BY THE SERVER'S REORDER (PUT
// /events/:id/lectures/order: every lecture id in the new order + the version
// the editor loaded; the server rebuilds the list from the STORED lectures and
// renumbers `order` 0..n-1). There is no Order box per lecture any more, and a
// whole-form save never changes the order: it only carries `expectedUpdatedAt`
// so a stale editor gets a 409 instead of overwriting newer links.
import { mapFieldErrors } from 'src/utils/apiErrorText';
import { zoomMessages } from 'src/sections/dashboard/zoom/zoomLabels';

type LectureLike = { _id?: string; order?: number | string | null };

const orderOf = (l: LectureLike | null | undefined): number => {
  const n = Number(l?.order);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The lectures in the order the program page shows them: by `order`, ties by
 * their place in the stored array (the website's own stable sort). The editor
 * opens in this order, so "Lecture N" is the website's position N.
 */
export function sortLecturesForEdit<T extends LectureLike>(
  lectures: readonly T[] | null | undefined
): T[] {
  return (lectures || [])
    .map((l, i) => ({ l, i }))
    .sort((a, b) => orderOf(a.l) - orderOf(b.l) || a.i - b.i)
    .map((x) => x.l);
}

/** The order a new lecture gets so it is last on the website: max + 1 (0 for the first). */
export function nextLectureOrder(lectures: readonly LectureLike[] | null | undefined): number {
  const list = lectures || [];
  return list.reduce((max, l) => Math.max(max, orderOf(l)), -1) + 1;
}

/**
 * Every lecture id with the lecture at `index` swapped one step (-1 up, +1
 * down). Null at the edges, and when a lecture has no id yet (a new lecture
 * must be saved before the order can change).
 */
export function swapIds(
  lectures: readonly LectureLike[] | null | undefined,
  index: number,
  dir: -1 | 1
): string[] | null {
  const list = lectures || [];
  const to = index + dir;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return null;
  const ids = list.map((l) => (l?._id ? String(l._id) : ''));
  if (ids.some((id) => !id)) return null;
  const out = ids.slice();
  out[index] = ids[to];
  out[to] = ids[index];
  return out;
}

// ----------------------------------------------------------------------
// The quiz clean-up. Questions and answers have no id and graded attempts are
// keyed by the QUESTION INDEX, so removing a blank in the middle would shift
// every later question under the attempts already taken. Only TRAILING blanks
// (what "Add question" / "Add answer" leave behind) are dropped; a blank in the
// middle is a field error the operator fixes.

export type FieldError = { path: string; message: string };

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

const answerIsBlank = (a: any): boolean => !text(a?.name) && !text(a?.name_ar);

export const QUESTION_REQUIRED = 'Question text is required';
export const ANSWER_REQUIRED = 'Answer text is required';

/**
 * `quiz` with trailing blank answers (per question) and trailing blank
 * questions removed, and the errors for blanks that cannot be removed. `base`
 * is the quiz's form path, e.g. `lectures.2.quiz`.
 */
export function cleanQuizForSave<Q extends { questions?: any[] } | null | undefined>(
  quiz: Q,
  base: string
): { quiz: Q; errors: FieldError[] } {
  if (!quiz || !Array.isArray(quiz.questions)) return { quiz, errors: [] };
  const errors: FieldError[] = [];
  const questions = quiz.questions.map((q: any) => {
    if (!q || !Array.isArray(q.answers)) return q;
    let last = q.answers.length - 1;
    while (last >= 0 && answerIsBlank(q.answers[last])) last -= 1;
    return last === q.answers.length - 1 ? q : { ...q, answers: q.answers.slice(0, last + 1) };
  });
  const questionIsBlank = (q: any): boolean =>
    !text(q?.title) && !text(q?.title_ar) && !(Array.isArray(q?.answers) && q.answers.length);
  let lastQ = questions.length - 1;
  while (lastQ >= 0 && questionIsBlank(questions[lastQ])) lastQ -= 1;
  const kept = questions.slice(0, lastQ + 1);
  kept.forEach((q: any, qi: number) => {
    const qPath = `${base}.questions.${qi}`;
    if (!text(q?.title)) errors.push({ path: `${qPath}.title`, message: QUESTION_REQUIRED });
    (Array.isArray(q?.answers) ? q.answers : []).forEach((a: any, ai: number) => {
      if (!text(a?.name)) {
        errors.push({ path: `${qPath}.answers.${ai}.name`, message: ANSWER_REQUIRED });
      }
    });
  });
  const same =
    kept.length === quiz.questions.length && kept.every((q, i) => q === quiz.questions![i]);
  return { quiz: same ? quiz : ({ ...quiz, questions: kept } as Q), errors };
}

// ----------------------------------------------------------------------
// A failed save. The axios interceptor rejects with the API's error BODY (no
// HTTP status), or with a string for a network error / an HTML gateway page.

export const PROGRAM_CHANGED_TEXT =
  'Someone else saved this program after you opened it. Nothing was saved.';
export const NETWORK_TEXT = 'The server could not be reached. Nothing was saved.';
export const RELOAD_DROPS_TEXT =
  'Reloading shows their version; your unsaved changes here are dropped.';
export const DIRTY_ORDER_TEXT = 'Save or undo your changes first to change the lecture order.';

export type SaveErrorInfo = {
  fieldErrors: FieldError[];
  lines: string[];
  conflict: boolean;
  network: boolean;
};

export function programSaveErrors(
  err: any,
  fallback = 'Could not save this program.'
): SaveErrorInfo {
  if (!err || typeof err === 'string') {
    return { fieldErrors: [], lines: [NETWORK_TEXT], conflict: false, network: true };
  }
  if (err.code === 'ProgramChanged') {
    const reason = String(err.detail?.reason || 'version');
    const lines =
      reason === 'version' ? [PROGRAM_CHANGED_TEXT] : zoomMessages(err, PROGRAM_CHANGED_TEXT);
    return { fieldErrors: [], lines, conflict: true, network: false };
  }
  if (Array.isArray(err.messages) && err.messages.length) {
    const { fields, other } = mapFieldErrors(err);
    const lines = [...fields.map((f) => `${pathLabel(f.path)}: ${f.message}`), ...other];
    return { fieldErrors: fields, lines, conflict: false, network: false };
  }
  return { fieldErrors: [], lines: zoomMessages(err, fallback), conflict: false, network: false };
}

// ----------------------------------------------------------------------
// Client validation errors on fields that have no input on the screen (a
// legacy lecture `order`, quiz internals) would otherwise block Save without a
// word: they go to the top Alert.

const RENDERED: RegExp[] = [
  /^(slug|title|title_ar|subtitle|subtitle_ar|description|description_ar|location|location_ar|date|ctaText|ctaText_ar|order)$/,
  /^lectures\.\d+\.(title|title_ar|speaker|speaker_ar|status|info|info_ar)$/,
  /^lectures\.\d+\.quiz\.(title|title_ar|passingScore|attemptsLimit|duration)$/,
  /^lectures\.\d+\.quiz\.questions\.\d+\.(title|title_ar)$/,
  /^lectures\.\d+\.quiz\.questions\.\d+\.answers\.\d+\.(name|name_ar)$/,
];

/** True when the form shows an input (and therefore the error) for this path. */
export function isRenderedPath(path: string): boolean {
  return RENDERED.some((re) => re.test(path));
}

/** "lectures.1.quiz.questions.0.title" → "Lecture 2 · question 1 · title". */
export function pathLabel(path: string): string {
  return path
    .replace(/^lectures\.(\d+)/, (_, n) => `Lecture ${Number(n) + 1}`)
    .replace(/^batches\.(\d+)/, (_, n) => `Batch ${Number(n) + 1}`)
    .replace(/questions\.(\d+)/g, (_, n) => `question ${Number(n) + 1}`)
    .replace(/answers\.(\d+)/g, (_, n) => `answer ${Number(n) + 1}`)
    .split('.')
    .join(' · ');
}

/** Every leaf of a react-hook-form `errors` object as `{ path, message }`. */
export function flattenErrors(errors: any, prefix = ''): FieldError[] {
  if (!errors || typeof errors !== 'object') return [];
  if (typeof errors.message === 'string' && (errors.type || errors.ref !== undefined)) {
    return [{ path: prefix, message: errors.message }];
  }
  const out: FieldError[] = [];
  Object.keys(errors).forEach((key) => {
    if (key === 'ref' || key === 'types') return;
    const child = errors[key];
    if (!child || typeof child !== 'object') return;
    out.push(...flattenErrors(child, prefix ? `${prefix}.${key}` : key));
  });
  return out;
}

/** The top-Alert lines for client errors whose field has no input on screen. */
export function invalidLines(
  errors: any,
  isRendered: (path: string) => boolean = isRenderedPath
): string[] {
  return flattenErrors(errors)
    .filter((e) => !isRendered(e.path))
    .map((e) => `${pathLabel(e.path)}: ${e.message}`);
}

// ----------------------------------------------------------------------
// "Has the operator changed anything?" — compared with what the editor loaded.
// Some fields of this form are written with setValue() without shouldDirty
// (the batches editor), so react-hook-form's own isDirty misses them; a
// reorder or a reload would then drop those edits without asking.

// An empty value is the same as a missing one: a field array mounted on a quiz
// with no `questions` key writes `[]` into the form values by itself, and that
// is not an edit.
const stable = (v: any): string =>
  JSON.stringify(v, (key, value) => {
    if (key === 'id' && typeof value === 'string') return undefined;
    if (value === null || value === '') return undefined;
    if (Array.isArray(value) && value.length === 0) return undefined;
    return value;
  });

export function differsFrom(current: any, loaded: any): boolean {
  return stable(current) !== stable(loaded);
}
